import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn } from "../../lib/utils";
import type { MmObject } from "../../api/mentalModels";
import { shortDate, sourceOf } from "../../lib/mmListSections";
import { mmTypeNameKey, mmTypeStyle } from "../../lib/mmTypeStyle";

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : s);

export const mmTypeName = (t: TFunction, type: string | undefined): string =>
  t(mmTypeNameKey(type), capitalise(type || "other")) as string;

/** "From a note · 3 Nov" / "From an insight or step" / "Added 3 Nov". */
export const mmSourceText = (t: TFunction, o: MmObject, language: string): string | null => {
  const src = sourceOf(o);
  const date = shortDate("date" in src ? src.date : undefined, language);
  if (src.kind === "note")
    return date
      ? (t("mm_source_note_dated", "From a note · {{date}}", { date }) as string)
      : (t("mm_source_note", "From a note") as string);
  if (src.kind === "takeaway") return t("mm_source_takeaway", "From an insight or step") as string;
  if (src.kind === "quality") return t("mm_source_quality", "From a quality") as string;
  return date ? (t("mm_source_created", "Added {{date}}", { date }) as string) : null;
};

const relDays = (iso: string, language: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  const days = Math.round(ms / 86_400_000);
  if (days < 1) return rtf.format(0, "day");
  if (days < 45) return rtf.format(-days, "day");
  return rtf.format(-Math.round(days / 30), "month");
};

/**
 * The model's meta line. Rows: `Fear · From a note · 3 Nov` (+ `· 2 visits`).
 * The page (`full`): adds `Not visited yet` or `· last 4 days ago`.
 */
export const mmMetaParts = (
  t: TFunction,
  o: MmObject,
  language: string,
  full = false
): string[] => {
  const parts: string[] = [mmTypeName(t, o.objectType)];
  const src = mmSourceText(t, o, language);
  if (src) parts.push(src);
  const visits = o.visitCount ?? 0;
  if (visits > 0) {
    parts.push(t("mm_row_visits", "{{count}} visits", { count: visits }) as string);
    if (full && o.lastVisitedOn)
      parts.push(t("mm_page_last_visit", "last {{time}}", { time: relDays(o.lastVisitedOn, language) }) as string);
  } else if (full) {
    parts.push(t("mm_page_not_visited", "Not visited yet") as string);
  }
  if (o.status === "parked") parts.push(t("mm_row_parked", "Parked") as string);
  return parts;
};

/** The type's icon tile (rows and the page header). */
export const MmTypeTile: React.FC<{ type: string | undefined; size?: "sm" | "md" }> = ({ type, size = "sm" }) => {
  const s = mmTypeStyle(type);
  const Icon = s.icon;
  return (
    <span
      aria-hidden
      className={cn(
        "flex items-center justify-center flex-none rounded-xl",
        size === "md" ? "h-10 w-10" : "h-9 w-9",
        s.tile
      )}
    >
      <Icon className={size === "md" ? "w-5 h-5" : "w-4 h-4"} />
    </span>
  );
};

interface Props {
  object: MmObject;
  selected?: boolean;
  onClick: () => void;
  /** e.g. the mobile chevron */
  trailing?: React.ReactNode;
  className?: string;
}

/**
 * One row for a mental model everywhere it is listed (Models tab, studio,
 * a space's Reflection list): type tile · words · meta line. The whole row is
 * the button; selected = soft primary tint.
 */
const MmRow: React.FC<Props> = ({ object, selected, onClick, trailing, className }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.split("-")[0] || "en";
  const words = object.label || object.workingStatement || object.statement;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full flex items-center gap-3 rounded-2xl border px-3 py-2.5 min-h-[44px] text-start",
        "transition-colors motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        selected
          ? "border-primary/20 bg-primary/[0.06]"
          : "border-transparent hover:bg-muted/50",
        className
      )}
    >
      <MmTypeTile type={object.objectType} />
      <span className="flex-1 min-w-0">
        <span dir="auto" className="block text-sm leading-snug text-foreground/90 line-clamp-2">
          {words}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5 truncate">
          {mmMetaParts(t, object, language).join(" · ")}
        </span>
      </span>
      {trailing}
    </button>
  );
};

export default MmRow;
